import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';
import { RealtimeChannel } from '@supabase/supabase-js';


export type OrderRequestItem = {
  name: string;
  quantity: number;
  product_id?: string;
  unit_price?: number; // in your example this is a number, adapt if cents
};
export interface OrderRequest {
  id: string;
  created_at: string;
  items: OrderRequestItem[];
  sub_total?: number;
  delivery_fee?: number;
  total_amount?: number;
  seller_count: number;
  confirmed_sellers: number;
  status: 'pending' | 'confirmed' | 'rejected' | 'paid' | 'processing';
  delivery_address: any;
  delivery_quote: any[];
  tracking_url?: string | null;
  buyer_id?: string;
  vendor_id?: string;
  parent_order_id?: string;
}

export function useOrderRequests() {
  const { user } = useAuth();
  const [orderRequests, setOrderRequests] = useState<OrderRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchOrderRequests = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      // 1) find this user's vendor row
      const { data: vendorRow, error: vErr } = await supabase
        .from('vendors')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (vErr) {
        console.error('Error querying vendors table:', vErr);
        throw new Error('Unable to access vendor information. Please try again.');
      }
      
      if (!vendorRow?.id) {
        console.log('No vendor account found for user:', user.id);
        // Set empty results instead of throwing error
        setOrderRequests([]);
        return;
      }

      // 2) fetch orders for that vendor
      const { data: directData, error: dErr } = await supabase
        .from('vendor_orders') // or 'orders' if that's your source table/view
        .select('*')
        .eq('vendor_id', vendorRow.id)   // ← use the UUID string
        .order('created_at', { ascending: false });

      console.log('Direct order requests data:', directData);
      console.log('Direct order requests error:', dErr);

      if (dErr) {
        console.error('Error querying vendor_orders table:', dErr);
        if (dErr.message.includes('relation') || dErr.message.includes('does not exist')) {
          throw new Error('Pending requests feature is not available. Please contact support.');
        } else if (dErr.message.includes('permission')) {
          throw new Error('You do not have permission to view pending requests.');
        } else {
          throw new Error('Unable to load pending requests. Please try again.');
        }
      }

      const processed = processOrderRequests(directData ?? []);
      setOrderRequests(processed);
    } catch (err: any) {
      console.error('Error fetching order requests:', err);
      
      // Provide user-friendly error messages
      let errorMessage = 'Failed to load pending requests';
      
      if (err.message) {
        if (err.message.includes('JSON object requested, multiple') || 
            err.message.includes('multiple (or no) rows returned')) {
          errorMessage = 'Multiple vendor accounts detected. Please contact support to resolve this issue.';
        } else if (err.message.includes('no rows returned')) {
          errorMessage = 'No vendor account found. Please set up your store first.';
        } else if (err.message.includes('network') || err.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else {
          errorMessage = err.message;
        }
      }
      
      setError(new Error(errorMessage));
    } finally {
      setLoading(false);
    }
  }, [user]);

  function processOrderRequests(data: any[]): OrderRequest[] {
    return (data || []).map((r) => {
      const sellerCount = Array.isArray(r.vendors) ? r.vendors.length : r.seller_count ?? 0;
      const confirmedSellers = Array.isArray(r.vendors)
        ? r.vendors.filter((v: any) => v.responded && v.status === 'accepted').length
        : r.confirmed_sellers ?? 0;

      return {
        ...r,
        seller_count: sellerCount,
        confirmed_sellers: confirmedSellers,
      };
    });
  }

  // Enhanced real-time subscription setup
  const setupRealtimeSubscription = useCallback(() => {
    let channel: RealtimeChannel | null = null;

    const initSubscription = async () => {
      if (!user) return;

      // Get vendor ID for filtering
      const { data: vendorRow } = await supabase
        .from('vendors')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (!vendorRow?.id) return;

      channel = supabase
        .channel(`vendor-orders-${user.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'vendor_orders',
            filter: `vendor_id=eq.${vendorRow.id}`,
          },
          (payload) => {
            console.log('🏪 Vendor order updated:', payload);
            const updatedOrder = payload.new;
            
            setOrderRequests(prevOrders => 
              prevOrders.map(order => 
                order.id === updatedOrder.id 
                  ? { 
                      ...order, 
                      // Update key fields that commonly change
                      status: updatedOrder.status,
                      tracking_url: updatedOrder.tracking_url,
                      total_amount: updatedOrder.total_amount,
                      sub_total: updatedOrder.sub_total,
                      delivery_fee: updatedOrder.delivery_fee,
                      items: updatedOrder.items || order.items,
                      // Preserve or recalculate processed fields if vendors data changed
                      seller_count: updatedOrder.vendors ? 
                        (Array.isArray(updatedOrder.vendors) ? updatedOrder.vendors.length : updatedOrder.seller_count ?? order.seller_count) :
                        order.seller_count,
                      confirmed_sellers: updatedOrder.vendors ?
                        (Array.isArray(updatedOrder.vendors) ? 
                          updatedOrder.vendors.filter((v: any) => v.responded && v.status === 'accepted').length :
                          updatedOrder.confirmed_sellers ?? order.confirmed_sellers) :
                        order.confirmed_sellers
                    }
                  : order
              )
            );
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'vendor_orders',
            filter: `vendor_id=eq.${vendorRow.id}`,
          },
          (payload) => {
            console.log('🏪 New vendor order:', payload);
            // For new orders, we need to process them through the helper function
            const newOrder = processOrderRequests([payload.new])[0];
            
            setOrderRequests(prevOrders => [newOrder, ...prevOrders]);
          }
        )
        .subscribe((status) => {
          console.log('Vendor orders subscription status:', status);
        });
    };

    initSubscription();

    return () => {
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user, processOrderRequests]);

  useEffect(() => {
    if (!user) return;

    fetchOrderRequests();
  }, [user, fetchOrderRequests]);

  useEffect(() => {
    const cleanup = setupRealtimeSubscription();
    return cleanup;
  }, [setupRealtimeSubscription]);

  return { orderRequests, loading, error, refetch: fetchOrderRequests };
}